import { useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import rehypeHighlight from 'rehype-highlight'
import type { Plugin } from 'unified'
import type { Root } from 'mdast'
import { ExternalLink } from 'lucide-react'
import { cn } from '@/lib/utils'
import { chatMarkdownUrlTransform, isAllowedExternalChatLink } from '@/lib/chat-markdown-links'
import { hrefForInAppNavigation } from '@/lib/maater-document-url'
import { InAppRouteLink } from '@/components/features/shared/in-app-route-link'
import { CodeBlock } from '@/components/layout/chat-message-code-block'
import { MermaidDiagram } from '@/components/features/mermaid-diagram'
import { testIds } from '@/lib/test-ids'

/** Preserve doc: protocol in links (same as assistant-message). */
export const remarkPreserveDocLinks: Plugin<[], Root> = () => {
  return (tree) => {
    const visit = (node: any) => {
      if (node.type === 'link' && node.url?.startsWith('doc:')) {
        node.data = node.data || {}
        node.data.href = node.url
        node.data.docId = node.url.replace('doc:', '')
      }
      if (node.children) {
        node.children.forEach(visit)
      }
    }
    visit(tree)
  }
}

const CHAT_PROSE_CLASSES = [
  'prose dark:prose-invert min-w-0 max-w-full w-full overflow-x-hidden text-[15px] leading-[1.55]',
  'prose-p:my-1 prose-headings:mt-2.5 prose-headings:mb-1 prose-headings:leading-snug',
  'prose-headings:text-foreground prose-strong:text-foreground',
  'prose-ul:my-1 prose-ol:my-1 prose-li:my-0 prose-li:leading-[1.5]',
  'prose-blockquote:my-1.5 prose-hr:my-2.5',
  'prose-pre:my-0 prose-pre:p-0 prose-pre:bg-transparent prose-pre:overflow-x-auto',
].join(' ')

/**
 * Side-panel density - matches `.document-reading-prose--panel`
 * (document list preview / Basecamp work-spec drill-in).
 */
const PANEL_PROSE_CLASSES = [
  'text-[13px] leading-[1.55]',
  'prose-p:my-1.5 prose-p:leading-relaxed',
  'prose-headings:font-semibold prose-headings:text-foreground',
  'prose-h1:text-[1.0625rem] prose-h1:leading-[1.35] prose-h1:mt-[0.35em] prose-h1:mb-[0.35em]',
  'prose-h2:text-[0.9375rem] prose-h2:leading-[1.35] prose-h2:mt-[1em] prose-h2:mb-[0.35em]',
  'prose-h3:text-[0.8125rem] prose-h3:leading-[1.4] prose-h3:mt-[0.85em] prose-h3:mb-[0.3em]',
  'prose-h4:text-xs prose-h5:text-xs prose-h6:text-xs prose-h4:mt-[0.75em] prose-h4:mb-[0.25em]',
  '[&>:is(h1,h2,h3):first-child]:mt-0',
  'prose-hr:my-5',
].join(' ')

export type MarkdownProseLinkMode = 'inbox' | 'chat-safe'
export type MarkdownProseDensity = 'chat' | 'panel'

export interface MarkdownProseProps {
  content: string
  className?: string
  /** data-testid on the prose root */
  testId?: string
  /**
   * inbox: allow all http(s) externals (trusted automation bodies).
   * chat-safe: Basecamp/GitLab allowlist only (hallucination guard).
   */
  linkMode?: MarkdownProseLinkMode
  /**
   * chat (default): AI chat body scale.
   * panel: secondary-panel reading density (same heading scale as document preview).
   */
  density?: MarkdownProseDensity
}

/**
 * Shared markdown renderer aligned with webapp AI chat prose:
 * GFM tables, code blocks, Mermaid, /doc/{uuid} links, doc: links.
 */
export function MarkdownProse({
  content,
  className,
  testId,
  linkMode = 'inbox',
  density = 'chat',
}: MarkdownProseProps) {
  const [copiedCode, setCopiedCode] = useState<string | null>(null)

  const onCopyCode = (code: string) => {
    void navigator.clipboard.writeText(code)
    setCopiedCode(code)
    window.setTimeout(() => setCopiedCode(null), 2000)
  }

  if (!content.trim()) return null

  return (
    <div
      className={cn(
        CHAT_PROSE_CLASSES,
        density === 'panel' && PANEL_PROSE_CLASSES,
        className,
      )}
      data-testid={testId}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkPreserveDocLinks]}
        rehypePlugins={[rehypeHighlight]}
        urlTransform={chatMarkdownUrlTransform}
        components={{
          pre: ({ children }) => <>{children}</>,
          table: ({ children, ...props }) => {
            const { node: _n, ...rest } = props as { node?: unknown; children?: ReactNode }
            return (
              <div className="not-prose my-2 w-full min-w-0 max-w-full overflow-x-auto rounded-md border border-border">
                <table className="w-full table-fixed text-left text-[15px] border-collapse" {...rest}>
                  {children}
                </table>
              </div>
            )
          },
          thead: ({ children, ...props }) => {
            const { node: _n, ...rest } = props as { node?: unknown; children?: ReactNode }
            return (
              <thead className="bg-muted/50 border-b border-border" {...rest}>
                {children}
              </thead>
            )
          },
          tbody: ({ children, ...props }) => {
            const { node: _n, ...rest } = props as { node?: unknown; children?: ReactNode }
            return (
              <tbody className="divide-y divide-border/50" {...rest}>
                {children}
              </tbody>
            )
          },
          tr: ({ children, ...props }) => {
            const { node: _n, ...rest } = props as { node?: unknown; children?: ReactNode }
            return (
              <tr className="hover:bg-muted/30 transition-colors" {...rest}>
                {children}
              </tr>
            )
          },
          th: ({ children, ...props }) => {
            const { node: _n, ...rest } = props as { node?: unknown; children?: ReactNode }
            return (
              <th className="min-w-0 px-3 py-2 font-semibold text-foreground/90 break-words [overflow-wrap:anywhere]" {...rest}>
                {children}
              </th>
            )
          },
          td: ({ children, ...props }) => {
            const { node: _n, ...rest } = props as { node?: unknown; children?: ReactNode }
            return (
              <td className="min-w-0 px-3 py-2 text-foreground/80 align-top break-words [overflow-wrap:anywhere] [&_code]:whitespace-pre-wrap [&_code]:break-words [&_a]:break-words" {...rest}>
                {children}
              </td>
            )
          },
          p: ({ children, ...props }) => (
            <div className="my-1" {...props}>
              {children}
            </div>
          ),
          code: (props) => {
            const { inline, children, className: codeClass, ...rest } = props as {
              inline?: boolean
              children?: ReactNode
              className?: string
              node?: unknown
              [key: string]: unknown
            }
            const codeString = String(children ?? '').replace(/\n$/, '')
            const language = codeClass?.replace('language-', '') || 'code'
            if (!inline && language === 'mermaid') {
              return (
                <div
                  className="my-2 not-prose w-full max-w-full overflow-hidden"
                  data-testid={testIds.mermaid.chatBlock}
                >
                  <MermaidDiagram
                    source={codeString}
                    showSourceToggle
                    className="chat-mermaid-diagram"
                  />
                </div>
              )
            }
            return (
              <CodeBlock
                inline={!!inline}
                copiedCode={copiedCode}
                onCopy={onCopyCode}
                className={codeClass}
                {...rest}
              >
                {children}
              </CodeBlock>
            )
          },
          a: ({ href, children, node, ...props }) => {
            const { node: _, ...domProps } = props as Record<string, unknown>
            const url = (href || '').trim()
            const docIdFromData = (node as { data?: { docId?: string } } | undefined)?.data?.docId

            if (docIdFromData) {
              return (
                <Link
                  to={`/doc/${docIdFromData}`}
                  className="text-primary hover:underline font-medium"
                  {...domProps}
                >
                  {children}
                </Link>
              )
            }

            if (url.startsWith('doc:')) {
              const id = url.slice(4)
              return (
                <Link
                  to={`/doc/${id}`}
                  className="text-primary hover:underline font-medium"
                  {...domProps}
                >
                  {children}
                </Link>
              )
            }

            const inAppHref = hrefForInAppNavigation(url)
            if (inAppHref) {
              return (
                <InAppRouteLink
                  to={inAppHref}
                  className="text-primary hover:underline font-medium"
                  {...domProps}
                >
                  {children}
                </InAppRouteLink>
              )
            }

            if (url.startsWith('http://') || url.startsWith('https://')) {
              if (linkMode === 'chat-safe') {
                if (!isAllowedExternalChatLink(url)) {
                  let host: string | null = null
                  try {
                    host = new URL(url).hostname
                  } catch {
                    host = null
                  }
                  return (
                    <span
                      className="text-muted-foreground line-through decoration-dotted cursor-not-allowed"
                      title={`External link blocked: ${host ?? url}`}
                    >
                      {children}
                    </span>
                  )
                }
              }
              return (
                <a
                  href={url}
                  className={cn(
                    'text-primary hover:underline font-medium',
                    linkMode === 'chat-safe'
                      ? 'inline break-all [overflow-wrap:anywhere]'
                      : 'inline-flex items-center gap-0.5',
                  )}
                  {...domProps}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {children}
                  <ExternalLink
                    className={cn(
                      'inline h-3 w-3 ml-0.5 opacity-60 flex-shrink-0',
                      linkMode === 'chat-safe' && 'align-text-bottom',
                    )}
                  />
                </a>
              )
            }

            return <span className="text-muted-foreground">{children}</span>
          },
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  )
}
